import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getScreens } from "../../../Redux/Actions/screenAction";
import { playlistToMultipleScreen } from "../../../Redux/Actions/playlistAction";
import toast from "react-hot-toast";
import { tr } from "../../../i18n";

const SCREEN_ASSIGN_FETCH_LIMIT = 10000;

function AssignScreensDialog({
  open,
  onClose,
  playlist,
  userId,
  onAssigned,
}) {
  const dispatch = useDispatch();
  const { screens, screenLoading } = useSelector((state) => state.Screen);
  const [selectedScreens, setSelectedScreens] = useState([]);

  useEffect(() => {
    if (open && userId) {
      dispatch(
        getScreens(userId, { page: 1, limit: SCREEN_ASSIGN_FETCH_LIMIT })
      );
    }
  }, [open, userId, dispatch]);

  useEffect(() => {
    if (!open) {
      setSelectedScreens([]);
      return;
    }
    if (playlist?.mac_list?.length && screens.length) {
      const assignedIds = screens
        .filter((s) => playlist.mac_list.includes(s.mac))
        .map((s) => String(s._id));
      setSelectedScreens(assignedIds);
    } else if (screens.length && !playlist?.mac_list?.length) {
      setSelectedScreens([]);
    }
  }, [open, playlist, screens]);

  const handleClose = () => {
    setSelectedScreens([]);
    onClose();
  };

  const handleScreenSelection = (screenId, isSelected) => {
    const id = String(screenId);
    setSelectedScreens((prev) => {
      const has = prev.some((s) => String(s) === id);
      if (isSelected) return has ? prev : [...prev, id];
      return prev.filter((s) => String(s) !== id);
    });
  };

  const handleToggleSelectAll = (checked) => {
    if (!checked || screens.length === 0) {
      setSelectedScreens([]);
      return;
    }
    setSelectedScreens(screens.map((s) => String(s._id)));
  };

  const allSelected =
    screens.length > 0 && selectedScreens.length === screens.length;
  const someSelected =
    selectedScreens.length > 0 && selectedScreens.length < screens.length;

  const handleConfirm = () => {
    if (!playlist) return;

    const selectedSet = new Set(selectedScreens.map((s) => String(s)));
    const selectedMacAddresses = screens
      .filter((screen) => selectedSet.has(String(screen._id)))
      .map((screen) => screen.mac);

    const currentMacAddresses = playlist.mac_list || [];
    const macAddressesToRemove = currentMacAddresses.filter(
      (mac) => !selectedMacAddresses.includes(mac)
    );

    dispatch(
      playlistToMultipleScreen(
        playlist._id,
        selectedMacAddresses,
        macAddressesToRemove
      )
    );

    if (selectedScreens.length === 0) {
      toast.success(tr("Playlist removed from all screens"));
    } else if (selectedScreens.length > 1) {
      toast.success(
        `Shared playlist assigned to ${selectedScreens.length} screens`
      );
    } else {
      toast.success(tr("Playlist assigned to 1 screen"));
    }

    if (onAssigned) onAssigned(selectedMacAddresses);
    handleClose();
  };

  const assignedCount = playlist?.mac_list?.length || 0;
  const isShared = assignedCount > 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
        },
      }}
      sx={{ zIndex: 1300 }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
          pt: 2.5,
        }}
      >
        <Typography variant="h6" component="div" fontWeight={600}>
          {isShared ? tr("Manage Shared Playlist") : tr("Assign Playlist to Screens")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {tr("One playlist, many screens — add content once and every selected screen will show \"")}{playlist?.name}&quot;
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {tr("Available screens")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {screens.length} {tr("total ·")} {selectedScreens.length} {tr("selected")}
            {assignedCount > 0 && ` · ${assignedCount} currently assigned`}
          </Typography>
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        {screenLoading && open ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress
              size={36}
              sx={{ color: "var(--primary-color, #008080)" }}
            />
          </Box>
        ) : (
          <>
            <FormControlLabel
              sx={{ ml: 0, mb: 1, alignItems: "center" }}
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => handleToggleSelectAll(e.target.checked)}
                  color="primary"
                  disabled={screens.length === 0}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {tr("Select all screens (")}{screens.length})
                </Typography>
              }
            />
            <List sx={{ maxHeight: 360, overflow: "auto", py: 0 }} disablePadding>
              {screens.map((screen) => {
                const isSelected = selectedScreens.some(
                  (s) => String(s) === String(screen._id)
                );
                const isCurrentlyAssigned = playlist?.mac_list?.includes(
                  screen.mac
                );
                const otherPlaylist = screen.playlist &&
                  String(screen.playlist._id) !== String(playlist?._id)
                  ? screen.playlist.name
                  : null;

                return (
                  <ListItem
                    key={screen._id}
                    button
                    onClick={() =>
                      handleScreenSelection(screen._id, !isSelected)
                    }
                    sx={{
                      backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                      "&:hover": {
                        backgroundColor: isSelected ? "#bbdefb" : "#f5f5f5",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 44 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleScreenSelection(screen._id, !isSelected);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        screen.name ||
                        `Screen ${screen.mac?.slice(-4) ?? ""}`
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {tr("MAC:")} {screen.mac}
                          </Typography>
                          {isCurrentlyAssigned && (
                            <Typography variant="caption" color="primary">
                              {tr("Assigned to this playlist")}
                            </Typography>
                          )}
                          {otherPlaylist && !isCurrentlyAssigned && (
                            <Typography variant="caption" color="warning.main">
                              {tr("Currently on \"")}{otherPlaylist}{tr("\" — will be moved here")}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
              {screens.length === 0 && (
                <ListItem sx={{ py: 3 }}>
                  <ListItemText
                    primary="No screens available"
                    secondary="Add screens from the Screen page first, then assign playlists."
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              )}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          {tr("Cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{
            bgcolor: "var(--primary-color, #008080)",
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            "&:hover": { bgcolor: "var(--primary-hover, #006666)" },
          }}
        >
          {selectedScreens.length === 0
            ? tr("Remove from all screens")
            : `Assign to ${selectedScreens.length} screen${
                selectedScreens.length !== 1 ? "s" : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignScreensDialog;
